import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';

import { useFavorites, useIsFavorite, useToggleFavorite } from '../hooks/useFavorites';

interface FavoriteButtonProps {
  professionalId: string;
  size?: 'sm' | 'md';
  variant?: 'light' | 'dark';
}

export function FavoriteButton({ professionalId, size = 'sm', variant = 'light' }: FavoriteButtonProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { data: favorites = [] } = useFavorites();
  const { data: isFavorite = false } = useIsFavorite(isAuthenticated ? professionalId : undefined);
  const { add, remove } = useToggleFavorite();
  const [showLimit, setShowLimit] = useState(false);

  const isLoading = add.isPending || remove.isPending;
  const favoriteCount = favorites.length;
  const atLimit = favoriteCount >= 7 && !isFavorite;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isFavorite) {
      remove.mutate(professionalId);
    } else if (atLimit) {
      setShowLimit(true);
      setTimeout(() => setShowLimit(false), 3000);
    } else {
      add.mutate(professionalId);
    }
  };

  const iconSize = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';
  const btnSize = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';

  const baseClasses = isFavorite
    ? 'bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20'
    : variant === 'dark'
      ? 'bg-white/10 text-white/40 hover:bg-white/20 hover:text-white/70'
      : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600';

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`flex ${btnSize} items-center justify-center rounded-full transition-all duration-200 ${baseClasses} disabled:opacity-50`}
        aria-label={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        title={isAuthenticated ? `${favoriteCount}/7 preferiti` : 'Accedi per aggiungere ai preferiti'}
      >
        <svg
          className={iconSize}
          viewBox="0 0 24 24"
          fill={isFavorite ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
          />
        </svg>
      </button>
      {showLimit && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
          Limite massimo di 7 preferiti raggiunto
          <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-red-500" />
        </div>
      )}
    </div>
  );
}
